import useLayoutStore from "@/store/useLayoutStore";
import { tokenData } from "@/types";
import { RcFile } from "antd/es/upload";
import dayjs from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import sanitizeHtml from 'sanitize-html';

export function formatNumber(
  number: number,
  locale?: string,
  options?: Intl.NumberFormatOptions,
): string {
  try {
    return new Intl.NumberFormat(locale, options).format(number);
  } catch (error) {
    return number.toString(); // Return the original number as a fallback
  }
}

export function jwtDecode(token?: string): tokenData | null {
  if (typeof token !== "string") {
    return null;
  }

  const parts = token.split(".");
  if (parts.length < 2) {
    return null;
  }

  try {
    return JSON.parse(Buffer.from(parts[1], "base64").toString());
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

export type TUserType =
  | "MENTOR"
  | "MENTEE"
  | "SUPER_MENTOR"
  | "ALLY"
  | "FANS"
  | "SPONSOR";

export function userTypeConvert(
  type: TUserType,
  compareType?: TUserType | null,
  plural?: boolean,
): string {
  if (compareType) {
    const dataType: Record<string, string> = {
      "MENTOR-MENTEE": plural ? "Mentees" : "Mentee",
      "MENTEE-MENTOR": plural ? "Mentors" : "Mentor",
      "ALLY-ALLY": plural ? "Connections" : "Connection",
      "SUPER_MENTOR-MENTEE": plural ? "Sponsorees" : "Sponsoree",
      "MENTEE-SUPER_MENTOR": plural ? "Sponsors" : "Sponsor",
    };

    const dataIndex: string = `${type}-${compareType}`;

    return dataType?.[dataIndex];
  } else {
    const dataType = {
      MENTOR: plural ? "Mentors" : "Mentor",
      MENTEE: plural ? "Mentees" : "Mentee",
      SUPER_MENTOR: plural ? "Sponsors" : "Sponsor",
      SPONSOR: plural ? "Sponsorees" : "Sponsoree",
      ALLY: plural ? "Connections" : "Connection",
      FANS: "Wave",
    };

    return dataType?.[type];
  }

  // const dataType = {
  //   MENTOR: plural ? "Mentors" : "Mentor",
  //   MENTEE: plural ? "Mentees" : "Mentee",
  //   SUPER_MENTOR: plural ? "Sponsors" : "Sponsor",
  //   SPONSOR: plural ? "Sponsoree" : "Sponsor",
  //   ALLY: plural ? "Allies" : "Ally",
  //   FANS: "Fans",
  // };

  // if (type === "MENTEE" && (compareType === "SUPER_MENTOR" || compareType === "SPONSOR"))
  //   return plural ? "Sponsorees" : "Sponsoree";
  // return dataType?.[type] || type;
}

export function userTypeConvertReverse(
  type: "Mentor" | "Mentee" | "Sponsoree" | "Allies" | "Fans",
): string {
  const dataType = {
    Mentor: "MENTOR",
    Mentee: "MENTEE",
    Sponsor: "SUPER_MENTOR",
    SPONSOR: "SUPER_MENTOR",
    Sponsoree: "SUPER_MENTOR",
    SPONSOREE: "SUPER_MENTOR",
    Allies: "ALLY",
    Fans: "FANS",
  };
  return dataType?.[type] || type;
}

export function useKeySearch() {
  const { isSearchOpenedSet } = useLayoutStore();
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for the specific key combination (Command/Ctrl + /)
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        isSearchOpenedSet(true);
        // Perform your desired action here
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
}

export function capitalizeFirstLetter(
  string: string,
  defaultValue: string = "",
) {
  const loweredCase = string?.toLowerCase() || defaultValue;
  return loweredCase?.charAt(0).toUpperCase() + loweredCase?.slice(1);
}

export function formatNumberKM(number: number) {
  if (isNaN(number)) {
    return "Invalid Number";
  }

  if (number < 1000) {
    return number.toString();
  } else if (number < 1000000) {
    return (number / 1000).toFixed(1) + "K";
  } else {
    return (number / 1000000).toFixed(1) + "M";
  }
}

export const removeIntentionalError = (text: string) => {
  if (!text) return text;
  return text.replace(/\{IntentionalError\}:/g, "");
};

export function removeDuplicatesByKey<
  T extends Record<K, any>,
  K extends keyof T,
>(arr: T[], key: K): T[] {
  const uniqueKeys: Record<string, boolean> = {};
  const result: T[] = [];

  arr.forEach((current) => {
    const keyValue = current[key];
    if (!uniqueKeys[String(keyValue)]) {
      uniqueKeys[String(keyValue)] = true;
      result.push(current);
    }
  });

  return result;
}

export const tooltipHelperPoints = (key: string) => {
  switch (key) {
    case "Verified Skills":
      return {
        show: true,
        desc: "Earn points by verifying skills of others or by getting your skills verified.",
      };
    case "Recommendations":
      return {
        show: true,
        desc: "Earn points by recommending Circles, Events, and Connections to your colleagues.",
      };
    case "Invite People":
      return {
        show: true,
        desc: "Invite People points are earned when you invite people to Circles, Events, and the Circle of Champions.",
      };

    case "SKILL_VERIFICATION_REQUEST":
      return {
        show: true,
        desc: "The user will receive points once they successfully request skill verification. The user will also receive additional points from 'Verify Skill' in the 'View All Milestones' tab once the skill is verified.",
      };

    case "VERIFY_SKILL":
      return {
        show: true,
        desc: "Both the verifier and the user whose skill is being verified will receive the same points once the skill is successfully verified.",
      };
    default:
      return { show: false, desc: "" };
  }
};

export function scrollTo(id: string) {
  const element = document.getElementById(id);

  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  } else {
    console.warn(`Element with id "${id}" not found.`);
  }
}

export function truncateText(
  text: string,
  maxLengths: {
    sm: number;
    lg: number;
  },
  suffix: string = "...",
): ReactNode | string {
  if (!text) return text;
  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    if (text.length <= maxLengths.sm) return text;
    return text.substring(0, maxLengths.sm - suffix.length) + suffix;
  } else {
    if (text.length <= maxLengths.lg) return text;
    return text.substring(0, maxLengths.lg - suffix.length) + suffix;
  }
}

export function getGMTOffsetFromTimezone(
  timezone: string,
  withoutGMT?: boolean,
) {
  // Use a fixed date for timezone calculations. This date should ideally be in the current year to account for DST changes.
  const referenceDate = "2024-01-01T00:00:00Z"; // January 1st to avoid DST issues
  const rawTimezone = timezone?.includes("___")
    ? timezone.split("___")?.[0]
    : timezone;
  const finalTimezone = rawTimezone === "null" ? "UTC" : rawTimezone;
  // Parse the reference date in the given timezone
  const localTime = dayjs.tz(referenceDate, finalTimezone);

  // Calculate the UTC offset in minutes
  const offsetMinutes = localTime.utcOffset();
  const offsetHours = Math.abs(offsetMinutes) / 60;
  const offsetMinutesRemainder = Math.abs(offsetMinutes) % 60;
  const offsetSign = offsetMinutes > 0 ? "+" : "-";

  // Format the offset as GMT±hh:mm
  const offsetFormatted = `GMT${offsetSign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutesRemainder).padStart(2, "0")}`;
  const offsetFormattedNoGMT = `${offsetSign}${String(offsetHours).padStart(2, "0")}:${String(offsetMinutesRemainder).padStart(2, "0")}`;

  return withoutGMT ? offsetFormattedNoGMT : offsetFormatted;
}

export function convertToLocalTime(utcTimestamp: string) {
  // Create a Date object from the UTC timestamp
  const utcDate = new Date(utcTimestamp);

  // Get the user's timezone offset in minutes
  const timezoneOffset = utcDate.getTimezoneOffset();

  // Convert the timezone offset to milliseconds
  const timezoneOffsetMillis = timezoneOffset * 60 * 1000;

  // Add the timezone offset to the UTC timestamp to get the local time
  const localTime = new Date(utcDate.getTime() + timezoneOffsetMillis);

  return localTime;
}

export function imageToBase64(file: File | RcFile): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // `reader.result` contains the Base64 string
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Failed to read file as base64"));
      }
    };

    reader.onerror = () => reject(new Error("Error reading file"));

    reader.readAsDataURL(file); // Read the image as a data URL (Base64)
  });
}

export function shuffleArray(array: Array<any>) {
  for (let i = array.length - 1; i > 0; i--) {
    // Generate a random index from 0 to i
    const j = Math.floor(Math.random() * (i + 1));
    // Swap elements at i and j
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

export function useGetImageOrientation(imageUrl: string) {
  const [orientation, setOrientation] = useState<
    "landscape" | "portrait" | "square"
  >("landscape");
  async function getOrientation() {
    return new Promise<"landscape" | "portrait" | "square">(
      (resolve, reject) => {
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => {
          if (img.width > img.height) {
            resolve("landscape");
          } else if (img.width < img.height) {
            resolve("portrait");
          } else {
            resolve("square");
          }
        };
        img.onerror = (error) => reject(error);
      },
    );
  }

  useEffect(() => {
    if (imageUrl) {
      (async () => {
        try {
          const orient = await getOrientation();
          setOrientation(orient);
        } catch (error) {
          // console.error("Failed to get image orientation:", error);
        }
      })();
    }
  }, [imageUrl]);

  return { orientation };
}

export function asyncWait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const convertTextContainingLink = (title: string) => {
    if (title?.includes("https://") || title?.includes("www.")) {
      const splittedTitle = title?.split(" ");
      const titleContainer = splittedTitle?.map((text, index) => {
        if (text?.includes("https://")) {
          return `<a
              href="${text}"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500"
            >
              ${text}${" "}
            </a>`
        } else if(text?.includes("www.")) {
          return `<a
              href="https://${text}"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500"
            >
              ${text}${" "}
            </a>`
        } else {
          return text;
        }
      });
      const clean = sanitizeHtml(titleContainer?.join(" ")?.toString(), {
        allowedTags: ["a"],
        allowedAttributes: {
          a: ["href", "target", "rel"],
        },
      });
      return clean;
    }
    return title;
  };